import { patchRequest, postRequest } from "../helpers/fetchHandler"
import { clearAuthTokens, setAuthTokens } from "../storage/authStorage"
import { clearPreferences } from "../storage/preferencesStorage"
import {
  REGISTER_LOADING,
  REGISTER_SUCCESS,
  REGISTER_FAILED,
  LOGIN_LOADING,
  LOGIN_FAILED,
  LOGIN_SUCCESS,
  LOG_OUT,
  REQUEST_RESET_EMAIL_LOADING,
  REQUEST_RESET_EMAIL_SUCCESS,
  REQUEST_RESET_EMAIL_FAILED,
  PASSWORD_RESET_LOADING,
  PASSWORD_RESET_FAILED,
  PASSWORD_RESET_SUCCESS,
  UPDATE_PROFILE_LOADING,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_FAILED,
  CHANGE_PASSWORD_LOADING,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILED
} from "../constants/actionTypes"

export function logout() {
  return async dispatch => {
    // Try to call the logout API to revoke refresh tokens
    // Do this before clearing local tokens so we have the auth header
    const url = "/api/v1/auth/logout"
    try {
      await postRequest({ url, auth: true, skipAuthHandling: true })
    } catch {
      // Ignore errors - we're logging out anyway
    }

    clearAuthTokens()
    await clearPreferences()
    dispatch({ type: LOG_OUT })
    return Promise.resolve()
  }
}

export function login(data) {
  return async dispatch => {
    let dispatched = false
    dispatch({ type: LOGIN_LOADING })

    const url = "/api/v1/auth/login"
    try {
      const response = await postRequest({ url, data })
      setAuthTokens(response)
      dispatch({ type: LOGIN_SUCCESS, payload: response, meta: { email: data.email } })
      dispatched = true
      return response
    } catch (error) {
      dispatch({ type: LOGIN_FAILED, error })
      dispatched = true
      throw error
    } finally {
      // Safety net: ensure loading state is always reset
      // This handles edge cases where neither SUCCESS nor FAILED was dispatched
      if (!dispatched) {
        dispatch({ type: LOGIN_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}

export function register(data) {
  return async dispatch => {
    let dispatched = false
    dispatch({ type: REGISTER_LOADING })

    const url = "/api/v1/auth/register/"
    try {
      const response = await postRequest({ url, data })
      dispatch({ type: REGISTER_SUCCESS, payload: { response }, meta: { email: data.email } })
      dispatched = true
      return response
    } catch (error) {
      dispatch({ type: REGISTER_FAILED, error })
      dispatched = true
      throw error
    } finally {
      // Safety net: ensure loading state is always reset
      // This handles edge cases where neither SUCCESS nor FAILED was dispatched
      if (!dispatched) {
        dispatch({ type: REGISTER_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}

export function requestResetEmail(data) {
  return async dispatch => {
    let dispatched = false
    dispatch({ type: REQUEST_RESET_EMAIL_LOADING })

    const url = "/api/v1/auth/request-reset-email"
    try {
      await postRequest({ url, data })
      dispatch({ type: REQUEST_RESET_EMAIL_SUCCESS })
      dispatched = true
      return Promise.resolve()
    } catch (error) {
      dispatch({ type: REQUEST_RESET_EMAIL_FAILED, error })
      dispatched = true
      throw error
    } finally {
      // Safety net: ensure loading state is always reset
      // This handles edge cases where neither SUCCESS nor FAILED was dispatched
      if (!dispatched) {
        dispatch({ type: REQUEST_RESET_EMAIL_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}

export function resetPassword(data) {
  return async dispatch => {
    let dispatched = false
    dispatch({ type: PASSWORD_RESET_LOADING })

    const url = "/api/v1/auth/reset-password"
    try {
      await postRequest({ url, data })
      dispatch({ type: PASSWORD_RESET_SUCCESS })
      dispatched = true
      return Promise.resolve()
    } catch (error) {
      dispatch({ type: PASSWORD_RESET_FAILED, error })
      dispatched = true
      throw error
    } finally {
      // Safety net: ensure loading state is always reset
      // This handles edge cases where neither SUCCESS nor FAILED was dispatched
      if (!dispatched) {
        dispatch({ type: PASSWORD_RESET_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}

export function updateProfile(data) {
  return async (dispatch, getState) => {
    let dispatched = false
    dispatch({ type: UPDATE_PROFILE_LOADING })

    const currentEmail = getState().auth.user?.email
    const url = "/api/v1/profile/user"
    try {
      const response = await patchRequest({ url, data, auth: true })
      const emailChanged = currentEmail !== response.email
      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: response
      })
      dispatched = true
      return { ...response, emailChanged }
    } catch (error) {
      dispatch({ type: UPDATE_PROFILE_FAILED, error })
      dispatched = true
      throw error
    } finally {
      if (!dispatched) {
        dispatch({ type: UPDATE_PROFILE_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}

export function changePassword(data) {
  return async dispatch => {
    let dispatched = false
    dispatch({ type: CHANGE_PASSWORD_LOADING })

    const url = "/api/v1/profile/password"
    try {
      await patchRequest({ url, data, auth: true })
      dispatch({ type: CHANGE_PASSWORD_SUCCESS })
      dispatched = true
      return Promise.resolve()
    } catch (error) {
      dispatch({ type: CHANGE_PASSWORD_FAILED, error })
      dispatched = true
      throw error
    } finally {
      if (!dispatched) {
        dispatch({ type: CHANGE_PASSWORD_FAILED, error: new Error("Request failed unexpectedly") })
      }
    }
  }
}
